import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';
import React from 'react';
import { getResizedText } from 'src/utils/utilities';
import {
  AUTHOR_CHARACTER_LIMIT,
  getFormattedDate,
  TEXT_CHARACTER_LIMIT,
} from './annotation-utils';

interface AnnotationsModalListCardProps {
  annotation: any;
  /** Preferred: called on click with no args (parent uses index from closure). */
  onSelect?: () => void;
  /** Legacy: called with annotation; use when parent needs the annotation object. */
  handleSelectAnnotation?: (annotation: any) => void;
  selected?: boolean;
  /** When annotation has no authorString, show this (e.g. current user name) instead of "—". */
  authorFallback?: string;
}

const AnnotationsModalListCard = ({
  annotation,
  onSelect,
  handleSelectAnnotation,
  selected = false,
  authorFallback,
}: AnnotationsModalListCardProps) => {
  const authorRaw = annotation?.authorString?.trim() || authorFallback?.trim() || '—';
  const authorDisplay = getResizedText(authorRaw, AUTHOR_CHARACTER_LIMIT);
  const snippet = getResizedText(annotation?.text, TEXT_CHARACTER_LIMIT);
  const dateDisplay = getFormattedDate(annotation?.time ?? '');

  const handleClick = () => {
    if (onSelect) onSelect();
    else if (handleSelectAnnotation) handleSelectAnnotation(annotation);
  };

  return (
    <Card
      elevation={0}
      sx={{
        minWidth: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: selected ? 'action.selected' : 'background.paper',
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      <CardActionArea onClick={handleClick} sx={{ width: '100%', display: 'block' }}>
        <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
              {authorDisplay}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
              {dateDisplay}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.4 }}>
            {snippet}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default AnnotationsModalListCard;
